import type { IconName } from '../../components';
import type { FeatureFlagKey } from '../../config/featureFlags';
import type { CommissionPlanType } from './types';

/**
 * The flag the full affiliate management (directory and invite, campaigns, reports) is behind: generally available,
 * kept as a rollback switch. FTUX's affiliates piece reads it too.
 */
export const AFFILIATE_MANAGEMENT_FLAG: FeatureFlagKey = 'affiliate-management';

/** The three native surfaces, switched by a `?tab=` query param. */
export type AffiliateTab = 'affiliates' | 'campaigns' | 'reports';

export const AFFILIATE_TABS: { id: AffiliateTab; label: string; icon: IconName }[] = [
  { id: 'affiliates', label: 'Affiliates', icon: 'affiliates' },
  { id: 'campaigns', label: 'Campaigns', icon: 'megaphone' },
  { id: 'reports', label: 'Reports', icon: 'trending-up' },
];

export const isAffiliateTab = (value: string | null): value is AffiliateTab =>
  value === 'affiliates' || value === 'campaigns' || value === 'reports';

/**
 * Roster filter by the ASSIGNED commission plan type ("All" plus the four plan types and "No plan").
 * The assigned plan is read per-affiliate from `/commission-structure` (see `assignedPlan` in
 * mapping.ts) — distinct from the report-derived deal type.
 */
export type PlanFilter = CommissionPlanType | 'none' | 'all';

export const ROSTER_PLAN_FILTERS: { value: PlanFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'revshare', label: 'Rev Share' },
  { value: 'cpa', label: 'CPA' },
  { value: 'hybrid', label: 'Hybrid' },
  { value: 'fixed', label: 'Fixed' },
  { value: 'none', label: 'No plan' },
];

/** Non-default plan filters (i.e. everything except the `all` neutral state) that live in the URL. */
export const isPlanFilter = (value: string | null): value is CommissionPlanType | 'none' =>
  value === 'revshare' || value === 'cpa' || value === 'hybrid' || value === 'fixed' || value === 'none';

/** Display label for an assigned plan type (roster card + filter chips). */
export const PLAN_TYPE_LABEL: Record<CommissionPlanType, string> = {
  revshare: 'Rev Share',
  cpa: 'CPA',
  hybrid: 'Hybrid',
  fixed: 'Fixed',
};

/** Campaigns status filter. Defaults to `active` so paused campaigns are hidden until asked for. */
export type CampaignFilter = 'active' | 'paused' | 'all';

/** Campaign status-filter chips. `active` is the default (shown first, hides paused). */
export const CAMPAIGN_FILTERS: { value: CampaignFilter; label: string }[] = [
  { value: 'active', label: 'Active' },
  { value: 'paused', label: 'Paused' },
  { value: 'all', label: 'All' },
];

export const isCampaignFilter = (value: string | null): value is CampaignFilter =>
  value === 'active' || value === 'paused' || value === 'all';
